import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { EntityTypes } from 'constants/entities';
import { ICreate } from 'core/actions/admin/data';
import { createRequest } from 'core/services/admin';
import { ActionSectionKey } from 'types/app/inspector';

/**
 * A section of the slide inspector, asked for from outside it.
 *
 * Frame 31d puts the same four moves in two places: the accordion on the right,
 * and the prompt on the slide itself ("This slide is text. Add to it?"). The
 * frames are deliberate that these are one vocabulary rather than two — the
 * prompt is a shortcut into the panel, not a second way of doing the job — so
 * pressing Add media has to open Media on the right.
 *
 * The two ends are far apart in the tree (the prompt renders deep inside the
 * player, the accordion is a sibling of it a page up) and the phone reaches the
 * same list a third way, so the request lives in the store rather than in a
 * context spanning them.
 *
 * It is a request, not a mode: whoever acts on it clears it. Left set, it would
 * re-open the section every time the inspector remounted — on the next module,
 * after a preview, after a reload.
 */
export interface IInspectorState {
  section: ActionSectionKey | null;
  /**
   * Slides whose prompt the author has closed, by action id. Kept here rather
   * than in the prompt: the slide feed unmounts the prompt as soon as another
   * slide is current, so local state would bring it back on the way past.
   */
  dismissedPrompts: number[];
  /**
   * Slides made in this session, by action id.
   *
   * The prompt is for the author who has just made a slide and does not yet
   * know what it can become. On a slide that already exists it is a panel over
   * work in progress, so it is offered only here — a session's own creations —
   * and never on a module opened cold, however empty its slides are.
   */
  createdActions: number[];
}

const initialState: IInspectorState = { section: null, dismissedPrompts: [], createdActions: [] };

export const inspectorSlice = createSlice({
  name: 'inspector',
  initialState,
  reducers: {
    openSection: (state, action: PayloadAction<ActionSectionKey>) => {
      state.section = action.payload;
    },
    /** Called once the inspector has expanded it — see the note above. */
    sectionOpened: state => {
      state.section = null;
    },
    /** The author closed a slide's prompt — see `SlidePrompt`. */
    dismissPrompt: (state, action: PayloadAction<number>) => {
      if (!state.dismissedPrompts.includes(action.payload)) state.dismissedPrompts.push(action.payload);
    },
  },
  extraReducers: builder => {
    // A slide is new from the create that made it — the id only exists from
    // here on, so nothing older can be mistaken for one.
    builder.addCase(createRequest.fulfilled, (state, action) => {
      const { type } = action.meta.arg as ICreate;
      const id = action.payload?.id;

      if (type === EntityTypes.ACTION && typeof id === 'number' && !state.createdActions.includes(id))
        state.createdActions.push(id);
    });
  },
});

export const { openSection, sectionOpened, dismissPrompt } = inspectorSlice.actions;

export default inspectorSlice.reducer;
